import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { jsonrepair } from 'jsonrepair'

import { Observable, map, scan } from 'rxjs'

import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import { ChatMessage, SendFeedbackPayload } from '../../../types/ai.types'

export type AnswerPayload = { question: string; session_id?: string | null }

const SOURCES_IDENTIFIER = '{"sources":'
const DATA_IDENTIFIER = 'data: '

@Injectable({ providedIn: 'root' })
export class AiAssistantAPI extends ApiHttpService {
  #assistantV1 = `ai-assistant/api/v1/assistant`
  #assistantV2 = `ai-assistant/api/v2/assistant`

  constructor() {
    const baseUrl = inject<string>('BASE_URL' as any)
    const httpClient = inject(HttpClient)

    super(baseUrl, httpClient)
  }

  getAnswer(payload: AnswerPayload): Observable<Omit<ChatMessage, 'author' | 'feedback'>> {
    return this.stream(`${this.#assistantV2}/stream`, payload).pipe(
      map((response) => {
        if (response.startsWith(DATA_IDENTIFIER)) {
          return response
        }

        try {
          const responseObj = JSON.parse(response)

          if (responseObj?.detail?.error_message) {
            throw new Error(responseObj.detail.error_message)
          }
        } catch (e) {
          return e ?? response
        }
      }),
      scan(
        (acc, curr) =>
          `${acc}${
            curr
              .replace(/\r\n\r\n$/, '') // EOL fix
              .replaceAll(`\r\n\r\n${DATA_IDENTIFIER}`, '') // multiple lines fix
              .replaceAll(DATA_IDENTIFIER, '') // normal start identifier fix
          }`,
        ''
      ),
      map((answerStr) => {
        const { textStr, metaDataStr } = this.#parseAnswerStr(answerStr)

        return {
          message: textStr,
          ...this.#deserializeMetaData(metaDataStr),
        }
      })
    )
  }

  sendFeedback(payload: SendFeedbackPayload): Observable<string> {
    const { reason, feedbackType, message, requestId, sessionId } = payload

    return this.post(`${this.#assistantV1}/feedback`, {
      feedback: {
        reason,
        response: feedbackType,
        user_message: message,
      },
      request_id: requestId,
      session_id: sessionId,
    })
  }

  #parseAnswerStr(answerStr: string): { textStr: string; metaDataStr: string } {
    const sourcesIndex = answerStr.indexOf(SOURCES_IDENTIFIER)

    const textStr = sourcesIndex === -1 ? answerStr : answerStr.substring(0, sourcesIndex)
    const metaDataStr = sourcesIndex === -1 ? '' : answerStr.substring(sourcesIndex)

    return { textStr, metaDataStr }
  }

  #deserializeMetaData(
    metaDataStr: string
  ): Pick<ChatMessage, 'sources' | 'context'> | Record<string, never> {
    if (!metaDataStr) {
      return {}
    }

    try {
      const metaData = JSON.parse(jsonrepair(metaDataStr))

      return {
        sources: metaData.sources,
        context: { session_id: metaData.session_id, request_id: metaData.request_id },
      }
    } catch (e) {
      return {}
    }
  }
}
